// client/src/dnd/emitter_tool/hooks/overlay/shapes/line.js
// Wall-aware overlay line that wraps/reflects/halts based on wallResponse.
// Inputs (from OverlayManager):
//   args = {
//     origin: {x,y},
//     settings: {
//       lengthM? | radius? | width?,
//       angleDeg?,
//       wallResponse?: "stop"|"bounce"|"travel"|"cling"|"kill",
//       restitution?: number,
//       probeRadiusPx?: number,
//       lineWidthPx?: number
//     },
//     pxPerMeter,
//     walls: [{x1,y1,x2,y2}] (device-space)
//   }

const EPS = 1e-6;

function clamp(v, a, b) { return Math.max(a, Math.min(b, v)); }
function dot(ax, ay, bx, by) { return ax * bx + ay * by; }
function len(x, y) { return Math.hypot(x, y); }
function reflect(vx, vy, nx, ny, r = 1) {
  const d = dot(vx, vy, nx, ny);
  return { x: (vx - 2 * d * nx) * r, y: (vy - 2 * d * ny) * r };
}
function segDir(s) {
  const dx = s.x2 - s.x1, dy = s.y2 - s.y1;
  const L = Math.hypot(dx, dy) || 1;
  return { tx: dx / L, ty: dy / L, nx: -(dy / L), ny: (dx / L) };
}
function angleToVec(deg) {
  const a = (Number(deg) || 0) * Math.PI / 180;
  return { x: Math.cos(a), y: Math.sin(a) };
}

// Ray vs circle (end caps)
function rayCircleTOI(p0x, p0y, vx, vy, cx, cy, r) {
  const dx = p0x - cx, dy = p0y - cy;
  const a = vx * vx + vy * vy + EPS;
  const b = 2 * (vx * dx + vy * dy);
  const c = dx * dx + dy * dy - r * r;
  const disc = b * b - 4 * a * c;
  if (disc < 0) return null;
  const s = Math.sqrt(Math.max(0, disc));
  const t1 = (-b - s) / (2 * a);
  const t2 = (-b + s) / (2 * a);
  let t = Number.POSITIVE_INFINITY;
  if (t1 >= 0 && t1 <= 1) t = Math.min(t, t1);
  if (t2 >= 0 && t2 <= 1) t = Math.min(t, t2);
  if (!Number.isFinite(t)) return null;
  const px = p0x + vx * t, py = p0y + vy * t;
  const nx = (px - cx), ny = (py - cy);
  const L = Math.hypot(nx, ny) || 1;
  return { t, nx: nx / L, ny: ny / L, cx, cy };
}

// Sweep circle vs segment capsule (segment thickened by r)
function sweepCircleVsSegmentCapsule(p0x, p0y, p1x, p1y, r, x1, y1, x2, y2) {
  const vx = p1x - p0x, vy = p1y - p0y;

  const sx = x2 - x1, sy = y2 - y1;
  const segLen = Math.hypot(sx, sy);
  if (segLen < 1e-7) return rayCircleTOI(p0x, p0y, vx, vy, x1, y1, r);

  const tx = sx / segLen, ty = sy / segLen;
  const nx = -ty, ny = tx;

  const hits = [];

  const denom = vx * nx + vy * ny;
  if (Math.abs(denom) > 1e-8) {
    const s0 = ((p0x - x1) * nx + (p0y - y1) * ny);
    const cand = [ ( r - s0) / denom, (-r - s0) / denom ];
    for (const t of cand) {
      if (t < 0 || t > 1) continue;
      const qx = p0x + vx * t, qy = p0y + vy * t;
      const u = ((qx - x1) * tx + (qy - y1) * ty) / segLen;
      if (u >= -1e-6 && u <= 1 + 1e-6) {
        const side = ((qx - x1) * nx + (qy - y1) * ny) >= 0 ? 1 : -1;
        hits.push({
          t,
          nx: nx * side, ny: ny * side,
          cx: x1 + tx * clamp(u, 0, 1) * segLen,
          cy: y1 + ty * clamp(u, 0, 1) * segLen
        });
      }
    }
  }

  const cap1 = rayCircleTOI(p0x, p0y, vx, vy, x1, y1, r);
  const cap2 = rayCircleTOI(p0x, p0y, vx, vy, x2, y2, r);
  if (cap1) hits.push(cap1);
  if (cap2) hits.push(cap2);

  if (!hits.length) return null;
  let best = hits[0];
  for (let i = 1; i < hits.length; i++) if (hits[i].t < best.t) best = hits[i];
  return { hit: true, time: best.t, nx: best.nx, ny: best.ny, cx: best.cx, cy: best.cy };
}

// Tracer: builds polyline according to wallResponse
function tracePolyline({ start, dir, maxDist, walls, response, restitution = 1, probeR = 0, maxBounces = 16 }) {
  const pts = [{ x: start.x, y: start.y }];
  let cur = { x: start.x, y: start.y };
  let vx = dir.x * maxDist;
  let vy = dir.y * maxDist;
  let remain = 1.0;

  for (let iter = 0; iter < maxBounces && remain > 1e-5; iter++) {
    // Find earliest collision in this segment of motion
    let best = null, bestT = 1.0, bestSeg = null;
    const target = { x: cur.x + vx * remain, y: cur.y + vy * remain };
    for (const s of walls || []) {
      const hit = sweepCircleVsSegmentCapsule(cur.x, cur.y, target.x, target.y, probeR, s.x1, s.y1, s.x2, s.y2);
      if (!hit) continue;
      if (hit.time < bestT) { best = hit; bestT = hit.time; bestSeg = s; }
    }

    if (!best) {
      pts.push({ x: target.x, y: target.y });
      break;
    }

    const preX = cur.x + (vx * remain) * best.time;
    const preY = cur.y + (vy * remain) * best.time;

    // nudge to surface
    const sep = Math.max(0.01, probeR * 1.001);
    const hitX = preX + best.nx * sep;
    const hitY = preY + best.ny * sep;

    // draw up to hit
    if (pts.length === 0 || pts[pts.length - 1].x !== preX || pts[pts.length - 1].y !== preY) {
      pts.push({ x: preX, y: preY });
    }

    const remainingT = remain * (1 - best.time);

    switch ((response || "stop").toLowerCase()) {
      case "kill":
      case "stop": {
        // end at contact
        pts.push({ x: hitX, y: hitY });
        return pts;
      }
      case "bounce": {
        const rv = reflect(vx, vy, best.nx, best.ny, Math.max(0, restitution));
        // start next leg from contact point
        cur.x = hitX; cur.y = hitY;
        // velocity for the remainder
        vx = rv.x; vy = rv.y;
        remain = remainingT;
        // add bounce point
        pts.push({ x: hitX, y: hitY });
        continue;
      }
      case "travel": {
        // slide along tangent of the hit segment
        const { tx, ty } = segDir(bestSeg);
        const mag = len(vx, vy);
        const sign = dot(vx, vy, tx, ty) >= 0 ? 1 : -1;
        const slidex = tx * mag * sign;
        const slidey = ty * mag * sign;
        cur.x = hitX; cur.y = hitY;
        vx = slidex; vy = slidey;
        remain = remainingT;
        pts.push({ x: hitX, y: hitY });
        continue;
      }
      case "cling": {
        // like travel, but slow dramatically
        const { tx, ty } = segDir(bestSeg);
        const mag = len(vx, vy) * 0.15;
        const sign = dot(vx, vy, tx, ty) >= 0 ? 1 : -1;
        cur.x = hitX; cur.y = hitY;
        vx = tx * mag * sign; vy = ty * mag * sign;
        remain = remainingT;
        pts.push({ x: hitX, y: hitY });
        continue;
      }
      default: {
        // unknown response: just stop
        pts.push({ x: hitX, y: hitY });
        return pts;
      }
    }
  }

  return pts;
}

export default function drawLine(g, app, args) {
  const safeArgs = args || {};
  const origin = safeArgs.origin || { x: 0, y: 0 };
  const settings = safeArgs.settings || {};
  const pxPerMeter = Number.isFinite(safeArgs.pxPerMeter) ? safeArgs.pxPerMeter : 1;
  const walls = Array.isArray(safeArgs.walls) ? safeArgs.walls : [];
  const wallResponse = safeArgs.wallResponse;

  // length: prefer lengthM, then radius, then width (meters)
  const lengthM = Number.isFinite(settings.lengthM) ? Number(settings.lengthM)
    : Number.isFinite(settings.radius) ? Number(settings.radius)
    : Number.isFinite(settings.width) ? Number(settings.width)
    : 3;
  const maxDist = Math.max(0, lengthM) * Math.max(1, pxPerMeter);

  // angle
  const angleDeg = Number.isFinite(settings.angleDeg) ? settings.angleDeg : 0;
  const dir = angleToVec(angleDeg);

  const response = (settings.wallResponse || wallResponse || "stop").toLowerCase();
  const rest = Number.isFinite(settings.restitution) ? settings.restitution
    : (Number.isFinite(safeArgs.restitution) ? safeArgs.restitution : 1);
  const probeR = Number.isFinite(settings.probeRadiusPx) ? settings.probeRadiusPx
    : (Number.isFinite(safeArgs.probeRadiusPx) ? safeArgs.probeRadiusPx : 0);

  // polyline path
  const pts = tracePolyline({
    start: origin,
    dir,
    maxDist,
    walls,
    response,
    restitution: rest,
    probeR,
    maxBounces: 24,
  });

  // draw
  const w = Number.isFinite(settings.lineWidthPx) ? settings.lineWidthPx : 3;
  try { g.lineStyle({ width: w, color: 0x00ffff, alpha: 1 }); }
  catch { g.lineStyle(w, 0x00ffff, 1); }

  if (pts.length) {
    g.moveTo(pts[0].x, pts[0].y);
    for (let i = 1; i < pts.length; i++) g.lineTo(pts[i].x, pts[i].y);
  }
}
